import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, MapPin, MessageCircle, Phone, RotateCw, ShieldAlert, Sparkles } from "lucide-react";
import { services } from "../../content/services";
import { contact } from "../../content/site";
import { RichText } from "./RichText";

const Thinking = ({ startedAt }) => {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSecs(Math.floor((Date.now() - (startedAt ?? Date.now())) / 1000)), 1000);
    return () => clearInterval(t);
  }, [startedAt]);
  return (
    <span className="mt-1 flex items-center gap-2 text-xs text-stone-400" role="status" data-testid="concierge-thinking">
      <span className="inline-flex gap-1" aria-hidden="true">
        {[0, 1, 2].map((i) => <span key={i} className="h-1.5 w-1.5 animate-pulse rounded-full bg-orange-400" style={{ animationDelay: `${i * 150}ms` }} />)}
      </span>
      {secs >= 6 ? `Still working on it… ${secs}s` : "Thinking…"}
    </span>
  );
};

export const Bubble = ({ m, onRetry }) => {
  if (m.role === "user") {
    return <p className="ml-auto w-fit max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-orange-500/15 px-3.5 py-2 text-sm text-stone-100" data-testid="concierge-msg-user">{m.text}</p>;
  }
  return (
    <div className="w-fit max-w-[92%] rounded-2xl rounded-bl-sm border border-white/[0.06] bg-white/[0.04] px-3.5 py-2.5 text-sm leading-relaxed text-stone-200" data-testid={`concierge-msg-${m.source}`}>
      <span className="mb-1 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-stone-400">
        {m.source === "ai" ? <><Sparkles className="h-3 w-3 text-orange-300" /> AI-generated</> : "TechCare guide"}
      </span>
      {m.source === "ai" ? (m.text ? <RichText text={m.text} /> : null) : <p className="whitespace-pre-wrap">{m.text}</p>}
      {m.pending && !m.text && <Thinking startedAt={m.startedAt} />}
      {m.retry && !m.pending && (
        <button type="button" onClick={() => onRetry(m.id)} data-testid="concierge-retry-btn"
          className="mt-2 inline-flex h-8 items-center gap-1.5 rounded-full border border-orange-500/40 px-3 text-xs text-orange-100 hover:bg-orange-500/15">
          <RotateCw className="h-3.5 w-3.5" /> Retry
        </button>
      )}
    </div>
  );
};

export const SafetyCard = ({ text }) => (
  <div role="alert" className="flex gap-2 rounded-2xl border border-red-400/40 bg-red-500/10 p-3 text-sm text-red-100" data-testid="concierge-safety-notice">
    <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" /> <span>{text}</span>
  </div>
);

export const NoticeCard = ({ text }) => (
  <p className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-stone-400" data-testid="concierge-notice">{text}</p>
);

export const ServicesCard = ({ items, onQuote, onClose }) => (
  <ul className="grid gap-2" data-testid="concierge-services-list">
    {items.map((s) => (
      <li key={s.key} className="rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid={`concierge-service-${s.key}`}>
        <p className="text-sm font-medium text-stone-100">{s.title}</p>
        <p className="mt-1 text-xs leading-snug text-stone-400">{s.body}</p>
        <span className="mt-2 flex flex-wrap gap-3 text-xs">
          {s.link && <Link to={s.link} onClick={onClose} className="inline-flex items-center gap-1 text-orange-300 hover:text-orange-200" data-testid={`concierge-service-view-${s.key}`}>View service <ArrowUpRight className="h-3 w-3" /></Link>}
          <button type="button" onClick={() => onQuote(s.title)} className="text-stone-300 underline-offset-2 hover:text-white hover:underline" data-testid={`concierge-service-quote-${s.key}`}>Request a quote</button>
        </span>
      </li>
    ))}
  </ul>
);

export const ServicePicker = ({ initial = [], onDone }) => {
  const [picked, setPicked] = useState(initial);
  const toggle = (n) => setPicked((p) => (p.includes(n) ? p.filter((x) => x !== n) : [...p, n]));
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3" data-testid="concierge-service-picker">
      <div className="flex flex-wrap gap-1.5">
        {services.map((s) => (
          <button key={s.slug} type="button" aria-pressed={picked.includes(s.name)} onClick={() => toggle(s.name)} data-testid={`concierge-pick-${s.slug}`}
            className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${picked.includes(s.name) ? "border-orange-500 bg-orange-500 text-black" : "border-white/15 text-stone-300 hover:border-orange-500"}`}>{s.name}</button>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <button type="button" onClick={() => onDone(picked)} className="tc-btn-primary h-9 rounded-full px-4 text-xs font-semibold" data-testid="concierge-pick-continue">Continue</button>
        <button type="button" onClick={() => onDone([])} className="h-9 rounded-full border border-white/15 px-4 text-xs text-stone-300" data-testid="concierge-pick-skip">Not sure, skip</button>
      </div>
    </div>
  );
};

export const TrackCard = ({ onClose }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3 text-sm text-stone-300" data-testid="concierge-track-card">
    <p>Open the private tracking link from your confirmation email, or go to the tracking page and enter your reference and tracking code there.</p>
    <p className="mt-2 text-xs text-amber-200">For your security, please don't share tracking codes in this chat. The assistant can't see your request.</p>
    <Link to="/track" onClick={onClose} className="tc-btn-primary mt-3 inline-flex h-9 items-center gap-1.5 rounded-full px-4 text-xs font-semibold" data-testid="concierge-open-track">
      <MapPin className="h-3.5 w-3.5" /> Open tracking page
    </Link>
  </div>
);

const chip = "inline-flex h-8 items-center gap-1.5 rounded-full border border-white/15 px-3 text-xs text-stone-200 transition-colors hover:border-orange-500";

// Guided human-contact options. Links only open the visitor's own phone/WhatsApp/email app; nothing is sent automatically.
export const ContactCard = ({ onMessage }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3 text-sm text-stone-300" data-testid="concierge-contact-card">
    <p>A member of our team will be glad to help. Choose how you'd like to reach us.</p>
    <ul className="mt-3 grid gap-2.5">
      {contact.phones.map((p) => (
        <li key={p.id} className="rounded-xl border border-white/[0.06] bg-black/20 p-2.5" data-testid={`concierge-contact-${p.id}`}>
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-stone-400">{p.region} · Phone / WhatsApp</p>
          <p className="mt-0.5 text-stone-100">{p.display}</p>
          <span className="mt-2 flex flex-wrap gap-2">
            <a href={p.whatsapp} target="_blank" rel="noopener noreferrer" className={chip} data-testid={`concierge-contact-whatsapp-${p.id}`}><MessageCircle className="h-3.5 w-3.5 text-orange-300" /> WhatsApp</a>
            <a href={`tel:${p.tel}`} className={chip} data-testid={`concierge-contact-call-${p.id}`}><Phone className="h-3.5 w-3.5 text-orange-300" /> Call</a>
          </span>
        </li>
      ))}
    </ul>
    <div className="mt-3 flex flex-wrap gap-2">
      <button type="button" onClick={onMessage} className="tc-btn-primary h-9 rounded-full px-4 text-xs font-semibold" data-testid="concierge-contact-message">Send our team a message</button>
      <a href={`mailto:${contact.email}`} className="inline-flex h-9 items-center gap-1.5 rounded-full border border-white/15 px-4 text-xs text-stone-200 hover:border-orange-500" data-testid="concierge-contact-email">
        <Mail className="h-3.5 w-3.5" /> {contact.email}
      </a>
    </div>
    <p className="mt-2.5 text-xs text-stone-400" data-testid="concierge-contact-address">Nairobi office: {contact.address.lines.join(", ")}</p>
    <p className="mt-1.5 text-xs text-stone-400">WhatsApp opens a chat in your own app. The Concierge doesn't send messages for you.</p>
  </div>
);
